import React, { useState, useEffect, useMemo } from "react";
import * as bootstrap from "bootstrap";
import { Button, Alert } from "react-bootstrap";
import useAuthStore from "../../stores/auth-store.js";
import useModelMappingStore from "../../stores/model-mapping-store.js";
import {
  useFeatureData,
  useUpdateFeature,
  useSuperviseFeature,
} from "../../hooks/useModelMapping.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import { feature } from "@turf/turf";

const ModelMappingDetails = () => {
  // ==================== ZUSTAND STORE ====================
  const selectedFeature = useModelMappingStore(
    (state) => state.selectedFeature
  );
  const clearSelectedFeature = useModelMappingStore(
    (state) => state.clearSelectedFeature
  );

  // ==================== REACT QUERY HOOKS ====================
  const {
    data: featureData,
    isLoading,
    error,
  } = useFeatureData(selectedFeature?._layerFilename, selectedFeature?.TAZ);
  const { mutate: updateFeature, isPending: isUpdating } = useUpdateFeature();
  const { mutate: superviseFeature, isPending: isSupervising } =
    useSuperviseFeature();

  // ==================== LOCAL STATE ====================
  const user = useAuthStore((state) => state.user);
  const isAdmin =
    user?.role_name === "Management" ||
    user?.role_name === "Super Admin" ||
    user?.role_name === "CRD Management";

  // UI State
  const [editingField, setEditingField] = useState(null);

  // Form State
  const [updatedBy, setUpdatedBy] = useState("");
  const [updatedComment, setUpdatedComment] = useState("");
  const [supervisedBy, setSupervisedBy] = useState("");
  const [supervisedComment, setSupervisedComment] = useState("");
  const [isApproved, setIsApproved] = useState(false);

  // Feature Data State
  const [propertyDefinitions, setPropertyDefinitions] = useState([]);
  const [featureVersionHistory, setFeatureVersionHistory] = useState([]);
  const [displayVersionNumber, setDisplayVersionNumber] = useState(null);
  const [displayFeatureMetadata, setDisplayFeatureMetadata] = useState(null);
  const [totalVersions, setTotalVersions] = useState(0);
  const [currentVersionFeature, setCurrentVersionFeature] = useState([]);

  // Editing State
  const [editedValues, setEditedValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  // ==================== LOCAL VARIABLES ====================
  const hasChanges = Object.keys(editedValues).length > 0;
  const currentVersionNumber = featureData?.current_version?.version;
  const isViewingCurrentVersion = displayVersionNumber === currentVersionNumber;
  const isViewingPendingVersion =
    featureData?.version_history.filter(
      (v) => v.version === displayVersionNumber
    )[0]?.is_supervised === 0;
  const isViewingHistory = !isViewingCurrentVersion && !isViewingPendingVersion;
  const isPending = useMemo(() => {
    if (!featureVersionHistory || featureVersionHistory.length === 0)
      return false;
    return (
      featureVersionHistory.filter((v) => v.version === displayVersionNumber)[0]
        ?.is_approved === 0
    );
  }, [featureVersionHistory, displayVersionNumber]);
  const pendingExists = useMemo(() => {
    if (!featureVersionHistory || featureVersionHistory.length === 0)
      return false;
    return featureVersionHistory.some((v) => v.is_approved === 0);
  }, [featureVersionHistory]);

  // ==================== HANDLERS & HELPERS ====================
  const handleEditField = (originalKey) => {
    setEditingField(originalKey);
  };

  const handleFieldChange = (originalKey, newValue) => {
    setEditedValues((prev) => {
      const updated = { ...prev };
      const originalValue = originalValues[originalKey];

      // If value matches original, remove from editedValues
      if (String(newValue).trim() === String(originalValue).trim()) {
        delete updated[originalKey];
      } else {
        updated[originalKey] = newValue;
      }

      return updated;
    });
  };

  const handleSaveField = () => {
    setEditingField(null);
  };

  const handleCancelEdit = () => {
    const key = editingField;
    setEditedValues((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setEditingField(null);
  };

  const handleIsEditEnabled = () => {
    const newEditState = !isEditEnabled;

    // If disabling edit mode, reset all changes
    if (!newEditState) {
      setEditedValues({}); // Clear all edits
      setEditingField(null); // Close any active editing field
    }

    setIsEditEnabled(newEditState);
    console.log("Edit mode:", newEditState ? "enabled" : "disabled");
  };

  const handleCommitChanges = () => {
    if (!updatedBy.trim()) {
      alert("Please enter your full name.");
      return;
    }

    updateFeature(
      {
        filename: selectedFeature._layerFilename,
        taz: selectedFeature.TAZ,
        updatedFields: editedValues,
        updatedBy: updatedBy,
        updatedByUserId: user.id,
        updatedComment: updatedComment,
      },
      {
        onSuccess: () => {
          setUpdatedBy("");
          setUpdatedComment("");
          setEditedValues({});
          setIsEditEnabled(false);
          console.log("✅ Feature updated successfully");
        },
        onError: (error) => {
          console.error("❌ Update failed:", error);
        },
      }
    );
  };

  const handleSuperviseSubmit = () => {
    if (!supervisedBy.trim() || !supervisedComment.trim()) {
      alert("Please fill in all fields.");
      return;
    }

    console.log(displayFeatureMetadata);
    superviseFeature(
      {
        versionId: displayFeatureMetadata.version_id,
        isApproved: isApproved,
        supervisedBy: supervisedBy,
        supervisedByUserId: user.id,
        supervisedComment: supervisedComment,
        featureHeaderId: displayFeatureMetadata.feature_header_id,
        filename: displayFeatureMetadata.filename,
        taz: displayFeatureMetadata.TAZ,
      },
      {
        onSuccess: () => {
          setSupervisedBy("");
          setSupervisedComment("");
          setIsApproved(false);
          setIsEditEnabled(false);
          console.log("✅ Supervision completed successfully");
        },
        onError: (error) => {
          console.error("❌ Supervision failed:", error);
        },
      }
    );
  };

  const navigateToVersion = (versionNumber) => {
    const version = featureVersionHistory.find(
      (v) => v.version === versionNumber
    );

    if (version) {
      setDisplayVersionNumber(versionNumber);
      // Clear edits when viewing history
      setEditedValues({});
      return version;
    }

    return null;
  };

  const navigateToNextVersion = () => {
    if (displayVersionNumber < totalVersions) {
      return navigateToVersion(displayVersionNumber + 1);
    }
    return null;
  };

  const navigateToPreviousVersion = () => {
    if (displayVersionNumber > 1) {
      return navigateToVersion(displayVersionNumber - 1);
    }
    return null;
  };

  const canNavigateToVersion = (direction) => {
    if (direction === "next") {
      return displayVersionNumber < totalVersions;
    } else if (direction === "prev") {
      return displayVersionNumber > 1;
    }
    return false;
  };

  const handleVersionNavigate = (direction) => {
    if (direction === "next") {
      navigateToNextVersion();
    } else {
      navigateToPreviousVersion();
    }
  };

  const formatValue = (value, definition) => {
    if (!definition) return String(value);

    // If unit is ID, return as-is
    if (definition.unit === "ID") {
      return String(value);
    }

    const num = parseFloat(value);
    if (isNaN(num) || !isFinite(num)) {
      return String(value);
    }

    // Apply factor and operator
    let processedValue = num;
    if (definition.factor && definition.operator) {
      switch (definition.operator) {
        case "*":
          processedValue = num * definition.factor;
          break;
        case "/":
          processedValue = num / definition.factor;
          break;
        case "+":
          processedValue = num + definition.factor;
          break;
        case "-":
          processedValue = num - definition.factor;
          break;
        default:
          processedValue = num;
      }
    }

    // Format based on unit type
    const hasUnit = definition.unit && definition.unit !== "ID";
    if (hasUnit) {
      const unitLower = definition.unit.toLowerCase();
      if (
        unitLower.includes("sq") ||
        unitLower.includes("km") ||
        unitLower.includes("m")
      ) {
        return processedValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
    }

    // Default: integer with thousand separators
    return Math.round(processedValue).toLocaleString("en-US");
  };

  const getFieldLabel = (definition) => {
    const cleanName = definition.clean_name || definition.field_name;
    if (definition.unit && definition.unit !== "ID") {
      return `${cleanName} (${definition.unit})`;
    }
    return cleanName;
  };

  // ==================== LOCAL HOOKS  ====================
  // Load feature data into local state when featureData changes
  useEffect(() => {
    if (!featureData) return;

    // Store property definitions
    setPropertyDefinitions(featureData.property_definitions || []);

    // Store version history
    const versionHistory = featureData.version_history || [];
    setFeatureVersionHistory(versionHistory);
    setTotalVersions(versionHistory.length);

    // Set current version number (initially the latest)
    setDisplayVersionNumber(featureData.current_version.version);

    // Store original values from current version
    const originals = {};
    const definitions = featureData.property_definitions || [];
    if (definitions && featureData.current_version) {
      definitions
        .filter((def) => def.display === 1)
        .forEach((def) => {
          originals[def.field_name] =
            featureData.current_version.properties[def.field_name];
        });
    }
    setOriginalValues(originals);

    // Store metadata
    setDisplayFeatureMetadata({
      version: featureData.current_version.version,
      version_id: featureData.current_version.version_id,
      filename: featureData._layerFilename,
      createdAt: featureData.current_version.created_at,
      createdBy: featureData.current_version.created_by,
      feature_header_id: featureData.feature_header_id,
      TAZ: featureData.current_version.properties.TAZ,
      is_approved: featureData.current_version.is_approved,
      creationComment: featureData.current_version.creation_comment,
      supervisedComment: "",
    });

    // Store current version feature data
    const currentVersionObject = {
      ...featureData.current_version,
      feature_header_id: featureData.feature_header_id,
      filename: featureData.filename,
      taz: featureData.taz,
    };
    setCurrentVersionFeature(currentVersionObject);

    // Reset editing state
    setEditedValues({});
    setEditingField(null);
  }, [featureData]);

  // Get current version metadata
  const displayVersionMetadata = useMemo(() => {
    if (!featureVersionHistory || !displayVersionNumber) return null;
    return featureVersionHistory.find(
      (v) => v.version === displayVersionNumber
    );
  }, [featureVersionHistory, displayVersionNumber]);

  // Prepare properties data for display
  const propertiesData = useMemo(() => {
    if (!propertyDefinitions || !displayVersionMetadata) return [];

    const displayableFields = propertyDefinitions
      .filter((def) => def.display === 1)
      .sort((a, b) => a.order - b.order);

    return displayableFields.map((def) => {
      const value = displayVersionMetadata.properties[def.field_name];
      const isReadOnly = def.note === "read_only";

      return {
        property: getFieldLabel(def),
        value: formatValue(value, def),
        rawValue: value,
        originalKey: def.field_name,
        isReadOnly: isReadOnly,
        definition: def,
      };
    });
  }, [propertyDefinitions, displayVersionMetadata]);

  // Update featureMetadata when currentVersionNumber changes
  useEffect(() => {
    if (!displayVersionMetadata || !featureData) return;

    setDisplayFeatureMetadata({
      version: displayVersionMetadata.version,
      version_id: displayVersionMetadata.version_id,
      filename: currentVersionFeature.filename,
      createdAt: displayVersionMetadata.created_at,
      createdBy: displayVersionMetadata.created_by,
      feature_header_id: featureData.feature_header_id,
      TAZ: displayVersionMetadata.properties?.TAZ,
      is_approved: displayVersionMetadata.is_approved,
      creationComment: displayVersionMetadata.creation_comment,
      supervisedComment: displayVersionMetadata.supervised_comment,
    });
  }, [displayVersionMetadata, featureData]);

  // Clear edited values when switching versions
  useEffect(() => {
    setEditedValues({});
    setEditingField(null);
  }, [displayVersionNumber]);

  // Popover initialization with cleanup
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    const popoverInstances = [];
    if (popoverTriggerList) {
      popoverTriggerList.forEach(function (elm) {
        const popover = new bootstrap.Popover(elm);
        popoverInstances.push(popover);
      });
    }

    return () => {
      popoverInstances.forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          console.warn("Error disposing popover:", error);
        }
      });

      const remainingPopovers = document.querySelectorAll(".popover");
      remainingPopovers.forEach((el) => {
        try {
          el.remove();
        } catch (error) {
          console.warn("Error removing popover element:", error);
        }
      });
    };
  }, []);

  // ==================== RENDER ====================
  return (
    <div className="d-flex align-items-center justify-content-center w-100 h-100">
      <div className="row h-100 w-100">
        <div className="col-lg-12 p-0 d-flex flex-column gap-1 h-100 w-100">
          {/* Content Area */}
          {!selectedFeature ? (
            <div
              className="text-gray-600 text-center p-3"
              id="selected-element-info"
            >
              No element selected.
            </div>
          ) : isLoading ? (
            <div className="text-center p-3">
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="d-block mt-2 text-muted">
                Loading feature data...
              </small>
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Failed to load feature data. Please try again.
            </Alert>
          ) : (
            <div className="d-flex flex-column text-start p-3 gap-3">
              {/* Version Navigation Ribbon */}
              {featureVersionHistory && featureVersionHistory.length > 0 && (
                <div className="px-3 py-2 border-bottom bg-gray-900 d-flex align-items-center justify-content-between">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleVersionNavigate("prev")}
                    disabled={!canNavigateToVersion("prev")}
                  >
                    <i className="bi bi-chevron-left fs-5"></i>
                  </Button>

                  <div className="text-center flex-grow-1">
                    <small
                      className="text-muted d-block"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Version {displayVersionNumber || "Current"} of{" "}
                      {totalVersions}
                    </small>
                    <p
                      className="mb-1 text-white"
                      style={{ fontSize: "0.85rem", fontWeight: "500" }}
                    >
                      {displayVersionMetadata?.created_at
                        ? new Date(
                            displayVersionMetadata.created_at
                          ).toLocaleString()
                        : "N/A"}
                    </p>
                    <small
                      className="text-muted"
                      style={{ fontSize: "0.7rem" }}
                    >
                      by {displayVersionMetadata?.created_by || "Unknown"}
                    </small>
                  </div>

                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleVersionNavigate("next")}
                    disabled={!canNavigateToVersion("next")}
                  >
                    <i className="bi bi-chevron-right fs-5"></i>
                  </Button>
                </div>
              )}

              {/* Properties List */}
              <div className="divide-y divide-gray-700">
                <h6
                  className="text-white fw-bold mb-3"
                  style={{ fontSize: "0.875rem" }}
                >
                  Properties
                </h6>

                {propertiesData.map((item, index) => {
                  const isEditing = editingField === item.originalKey;
                  const hasEdit = editedValues.hasOwnProperty(item.originalKey);
                  const displayValue = hasEdit
                    ? editedValues[item.originalKey]
                    : item.rawValue;

                  return (
                    <div
                      key={index}
                      className={`d-flex align-items-center justify-content-between gap-3 p-1 border-bottom transition-all ${
                        isEditing ? "bg-primary bg-opacity-10 rounded" : ""
                      }`}
                      style={{
                        backgroundColor: isEditing ? undefined : "transparent",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!isEditing) {
                          e.currentTarget.classList.add("bg-theme");
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isEditing) {
                          e.currentTarget.classList.remove("bg-theme");
                        }
                      }}
                    >
                      {/* Label Section */}
                      <div>
                        <p className="text-muted mb-0 fs-6">{item.property}</p>
                      </div>

                      {/* Value Section */}
                      <div className="d-flex align-items-center gap-2 ms-auto">
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              className="form-control form-control-sm fs-6"
                              value={displayValue}
                              onChange={(e) =>
                                handleFieldChange(
                                  item.originalKey,
                                  e.target.value
                                )
                              }
                              autoFocus
                              style={{ width: "120px" }}
                            />
                            <button
                              className="btn btn-sm p-0 text-success fs-6"
                              onClick={handleSaveField}
                              title="Save"
                            >
                              <i className="fa fa-check fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm p-0 text-danger fs-5"
                              onClick={handleCancelEdit}
                              title="Reset"
                            >
                              <i className="bi bi-arrow-repeat fs-5"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <span
                              className={`text-end fs-6 d-flex align-items-center gap-2 ${
                                hasEdit ? "text-warning" : "text-white"
                              }`}
                            >
                              {formatValue(displayValue, item.definition)}
                              {hasEdit && (
                                <i className="bi bi-asterisk fs-6"></i>
                              )}
                              {!isViewingHistory &&
                                item.isReadOnly &&
                                !isPending &&
                                !pendingExists &&
                                isEditEnabled && (
                                  <Button
                                    variant="secondary"
                                    size="xs"
                                    onClick={(e) => e.preventDefault()}
                                    disabled
                                    title="Read-only field"
                                  >
                                    <i className="bi bi-lock-fill text-muted"></i>
                                  </Button>
                                )}
                              {!isViewingHistory &&
                                !item.isReadOnly &&
                                !isPending &&
                                !pendingExists &&
                                isEditEnabled && (
                                  <Button
                                    variant="primary"
                                    size="xs"
                                    onClick={() =>
                                      handleEditField(item.originalKey)
                                    }
                                    disabled={isEditing}
                                    title="Edit field"
                                  >
                                    <i className="bi bi-pencil-fill fs-6"></i>
                                  </Button>
                                )}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Metadata */}
              {displayFeatureMetadata && (
                <div className="px-3">
                  <small className="text-muted d-block mb-1">
                    Version: v{displayFeatureMetadata.version} | Last Modified:{" "}
                    {displayFeatureMetadata.createdAt
                      ? new Date(
                          displayFeatureMetadata.createdAt
                        ).toLocaleString()
                      : "N/A"}{" "}
                    | Modified By: {displayFeatureMetadata.createdBy} | Status:{" "}
                    {displayFeatureMetadata.is_approved ? (
                      <span className="text-success">Approved</span>
                    ) : (
                      <span className="text-danger">Not Approved</span>
                    )}{" "}
                    | Creation Comments:{" "}
                    {displayFeatureMetadata.creationComment || "N/A"} | Approval
                    Comments:{" "}
                    {displayFeatureMetadata.supervisedComment || "N/A"} |
                  </small>
                  {isPending && (
                    <small className="d-block text-warning">
                      Pending approval
                    </small>
                  )}
                </div>
              )}

              {/* Read-only indicator for history */}
              {isViewingHistory && (
                <div
                  className="alert alert-info mb-0 mx-3"
                  role="alert"
                  style={{ fontSize: "0.75rem" }}
                >
                  <i className="bi bi-info-circle me-2"></i>
                  Not current version
                </div>
              )}

              {/* Current version indicator */}
              {isViewingCurrentVersion && (
                <div
                  className="alert alert-success mb-0 mx-3"
                  role="alert"
                  style={{ fontSize: "0.75rem" }}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  This is the current active version
                </div>
              )}

              {/* Toggle Editing buttons */}
              {isViewingCurrentVersion && !isPending && !pendingExists && (
                // Button to enable editing
                <div className="px-3">
                  <Button
                    variant={`${
                      isEditEnabled ? "outline-danger" : "outline-primary"
                    }`}
                    size="sm"
                    onClick={() => handleIsEditEnabled()}
                    disabled={isPending || pendingExists}
                  >
                    {isEditEnabled ? "Disable Editing" : "Enable Editing"}
                  </Button>
                </div>
              )}

              {/* Commit Form Section */}
              {hasChanges && !isPending && !isViewingHistory && (
                <div className="px-3 p-3 border-top">
                  <h6
                    className="text-white fw-bold mb-3"
                    style={{ fontSize: "0.875rem" }}
                  >
                    Commit All Changes
                  </h6>

                  <div className="mb-3">
                    <label
                      htmlFor="updatedBy"
                      className="form-label text-white"
                      style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    >
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="updatedBy"
                      className="form-control form-control-sm"
                      value={updatedBy}
                      onChange={(e) => setUpdatedBy(e.target.value)}
                      placeholder="Enter your full name"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="updatedComment"
                      className="form-label text-white"
                      style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    >
                      Comment (Optional)
                    </label>
                    <textarea
                      id="updatedComment"
                      className="form-control form-control-sm"
                      value={updatedComment}
                      onChange={(e) => setUpdatedComment(e.target.value)}
                      placeholder="Describe your changes..."
                      rows="3"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </div>

                  <Button
                    variant="primary"
                    size="md"
                    className="w-100 mb-1 d-flex flex-row justify-content-center gap-1"
                    onClick={handleCommitChanges}
                    disabled={isUpdating || !updatedBy.trim()}
                  >
                    {isUpdating ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-floppy2-fill fs-6"></i>
                        Commit All Changes ({Object.keys(editedValues).length})
                      </>
                    )}
                  </Button>

                  {/* Changes Indicator */}
                  <div className="mb-0 gap-2 d-flex justify-content-center align-items-center">
                    <i className="bi bi-exclamation-triangle-fill text-warning fs-4"></i>
                    <span className="text-warning fs-6">
                      {Object.keys(editedValues).length} field(s) modified
                    </span>
                  </div>
                </div>
              )}

              {/* Supervision Section */}
              {isAdmin &&
                isPending &&
                !isViewingHistory &&
                isViewingPendingVersion && (
                  <div className="px-3 p-3 border-top">
                    <h6
                      className="text-white fw-bold mb-3"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Approve Changes
                    </h6>

                    <div className="mb-3">
                      <label
                        htmlFor="supervisedBy"
                        className="form-label text-white"
                        style={{ fontSize: "0.75rem", fontWeight: "500" }}
                      >
                        Approved By <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="supervisedBy"
                        className="form-control form-control-sm"
                        value={supervisedBy}
                        onChange={(e) => setSupervisedBy(e.target.value)}
                        placeholder="Enter full name"
                        style={{ fontSize: "0.75rem" }}
                      />
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="supervisedComment"
                        className="form-label text-white"
                        style={{ fontSize: "0.75rem", fontWeight: "500" }}
                      >
                        Comment <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="supervisedComment"
                        className="form-control form-control-sm"
                        value={supervisedComment}
                        onChange={(e) => setSupervisedComment(e.target.value)}
                        placeholder="Describe your decision..."
                        rows="3"
                        style={{ fontSize: "0.75rem" }}
                      />
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="decisionSwitch"
                          checked={isApproved}
                          onChange={(e) => setIsApproved(e.target.checked)}
                        />
                        <label
                          className="form-check-label text-white"
                          htmlFor="decisionSwitch"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {isApproved ? "✓ Approve" : "✕ Decline"}
                        </label>
                      </div>
                    </div>

                    <button
                      className="btn btn-primary btn-sm w-100"
                      onClick={handleSuperviseSubmit}
                      disabled={
                        isSupervising ||
                        !supervisedBy.trim() ||
                        !supervisedComment.trim()
                      }
                    >
                      {isSupervising ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check me-2"></i>
                          Submit Supervision
                        </>
                      )}
                    </button>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelMappingDetails;
